import React, { useState, useEffect, useRef } from 'react';
import { useNotifications } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';

function timeAgo(ts) {
  const diff = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (diff < 60) return `${diff}s`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

function typeIcon(type) {
  if (type === 'inbound_sms') return '💬';
  if (type === 'inbound_call') return '📞';
  if (type === 'ai_sms_reply') return '🤖';
  if (type === 'live_chat') return '🗨️';
  if (type === 'intake_new_lead') return '🌱';
  if (type === 'job' || type === 'job_update') return '🔧';
  if (type === 'payout' || type === 'payout_released') return '💰';
  if (type === 'new_sales_rep') return '👤';
  if (type === 'plan_upgrade') return '💳';
  if (type === 'deal_landed') return '🏆';
  if (type === 'commission_approval') return '💰';
  return '🔔';
}

function parseMeta(message) {
  if (!message) return null;
  if (message.includes('|~|')) {
    const idx = message.lastIndexOf('|~|');
    try { return JSON.parse(message.slice(idx + 3)); } catch {}
  }
  const phoneMatch = message.match(/^(\+?1?\d{10,13})\s*:/);
  if (phoneMatch) return { phone: phoneMatch[1] };
  return null;
}

function getDisplay(message) {
  if (!message) return '';
  if (message.includes('|~|')) return message.slice(0, message.lastIndexOf('|~|'));
  return message;
}

function routeForNotif(notif, role) {
  const meta = parseMeta(notif.message);
  const leadsPath = role === 'admin' ? '/dashboard/admin/leads' : '/dashboard/sales/pipeline';
  const prospectsPath = role === 'admin' ? '/dashboard/admin/ai-prospects' : '/dashboard/sales/ai-prospects';

  if (notif.type === 'live_chat') {
    return { path: '/dashboard/admin/live-chats', state: meta?.sid ? { openSessionId: meta.sid } : {} };
  }
  if (['inbound_sms', 'inbound_call', 'ai_sms_reply', 'intake_new_lead'].includes(notif.type)) {
    if (meta?.lid) return { path: leadsPath, state: { openLeadId: meta.lid, openToTab: 'comms' } };
    if (meta?.pid) return { path: prospectsPath, state: { openProspectId: meta.pid } };
    if (meta?.phone) return { path: leadsPath, state: { openLeadPhone: meta.phone, openToTab: 'comms' } };
    return { path: leadsPath, state: { openToTab: 'comms' } };
  }
  if (notif.type === 'payout_released' || notif.type === 'payout_processed') {
    const base = role === 'sales_rep' ? '/dashboard/sales' : role === 'admin' ? '/dashboard/admin' : '/dashboard/contractor';
    return { path: `${base}/payouts`, state: {} };
  }
  if (notif.type === 'new_sales_rep') {
    return { path: '/dashboard/admin/users', state: {} };
  }
  if (notif.type === 'plan_upgrade') {
    return { path: '/dashboard/admin/users', state: {} };
  }
  if (notif.type === 'deal_landed') {
    const meta = parseMeta(notif.message);
    return { path: '/dashboard/admin/leads', state: meta?.lid ? { openLeadId: meta.lid } : {} };
  }
  if (notif.type === 'commission_approval') {
    return { path: '/dashboard/admin/commission-approvals', state: {} };
  }
  const base = role === 'sales_rep' ? '/dashboard/sales' :
               role === 'admin' ? '/dashboard/admin' :
               role === 'contractor_admin' ? '/dashboard/contractor-admin' :
               role === 'contractor' ? '/dashboard/contractor' : '/dashboard';
  return { path: base, state: {} };
}

export default function NotifPanel({ role, navigate, onClose }) {
  const { notifications, count, soundEnabled, toggleSound, markAllRead, markOneRead, refresh } = useNotifications();
  const { user } = useAuth();
  const [expandedGroups, setExpandedGroups] = useState({});
  const [clearing, setClearing] = useState(false);
  const cleanedRef = useRef(false);

  // Run retroactive link cleanup once when panel first opens
  useEffect(() => {
    if (!user?.id || cleanedRef.current) return;
    cleanedRef.current = true;
    fetch('/api/notifications/patch-sms-meta', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id }),
    }).then(r => r.json()).then(result => {
      if (result.patched > 0) refresh();
    }).catch(() => {});
  }, [user?.id, refresh]);

  const handleNotifClick = (notif) => {
    if (!notif.read) markOneRead(notif.id);
    onClose();
    const { path, state } = routeForNotif(notif, role);
    navigate(path, { state });
  };

  const handleGroupClick = (e, groupKey, isProspect) => {
    e.stopPropagation();
    onClose();
    if (isProspect) {
      const prospectsPath = role === 'admin' ? '/dashboard/admin/ai-prospects' : '/dashboard/sales/ai-prospects';
      navigate(prospectsPath, { state: { openProspectId: groupKey } });
    } else {
      const leadsPath = role === 'admin' ? '/dashboard/admin/leads' : '/dashboard/sales/pipeline';
      navigate(leadsPath, { state: { openLeadId: groupKey, openToTab: 'comms' } });
    }
  };

  const toggleGroup = (key) => {
    setExpandedGroups(prev => ({ ...prev, [key]: !(prev[key] ?? false) }));
  };

  const handleClearRead = async () => {
    if (!user?.id || clearing) return;
    setClearing(true);
    try {
      await fetch('/api/notifications/delete-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      });
      await refresh();
    } catch {}
    setClearing(false);
  };

  // Bucket notifications into lead groups, prospect groups, and others
  const leadGroups = {};
  const prospectGroups = {};
  const nonGrouped = [];

  for (const n of notifications) {
    const meta = parseMeta(n.message);
    if (meta?.lid) {
      if (!leadGroups[meta.lid]) {
        leadGroups[meta.lid] = {
          key: meta.lid,
          name: meta.company || meta.client_name || 'Lead',
          items: [],
        };
      }
      leadGroups[meta.lid].items.push({ ...n, _meta: meta });
    } else if (meta?.pid) {
      if (!prospectGroups[meta.pid]) {
        prospectGroups[meta.pid] = {
          key: meta.pid,
          name: meta.company || meta.client_name || 'Prospect',
          items: [],
        };
      }
      prospectGroups[meta.pid].items.push({ ...n, _meta: meta });
    } else {
      nonGrouped.push(n);
    }
  }

  const leadGroupList = Object.values(leadGroups);
  const prospectGroupList = Object.values(prospectGroups);
  const hasGroups = leadGroupList.length > 0 || prospectGroupList.length > 0;
  const readCount = notifications.filter(n => n.read).length;

  const renderGroup = (group, isProspect) => {
    const unread = group.items.filter(n => !n.read).length;
    const isExpanded = expandedGroups[group.key] ?? false;
    const latestItem = group.items[0];
    const groupIcon = isProspect ? '🌱' : '👤';

    return (
      <div key={group.key} className="border-b border-theme/40 last:border-0">
        <div
          className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer hover:bg-white/5 transition-colors select-none"
          onClick={() => toggleGroup(group.key)}
        >
          <span className="text-base flex-shrink-0">{groupIcon}</span>
          <div className="flex-1 min-w-0">
            <button
              className="text-xs font-semibold text-primary hover:text-yellow-400 truncate block text-left transition-colors max-w-full"
              onClick={(e) => handleGroupClick(e, group.key, isProspect)}
              title={`Open ${group.name}`}
            >
              {group.name}
            </button>
            {!isExpanded && latestItem && (
              <p className="text-[10px] text-muted truncate leading-tight mt-0.5">
                {typeIcon(latestItem.type)} {getDisplay(latestItem.message).slice(0, 55)}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {unread > 0 && (
              <span className="text-[9px] font-bold text-white bg-red-500 rounded-full px-1.5 py-0.5 leading-none">
                {unread}
              </span>
            )}
            <span
              className="text-muted text-[8px] transition-transform duration-200"
              style={{ display: 'inline-block', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              ▼
            </span>
          </div>
        </div>

        {isExpanded && (
          <div style={{ background: 'rgba(0,0,0,0.12)' }}>
            {group.items.map(notif => (
              <button
                key={notif.id}
                onClick={() => handleNotifClick(notif)}
                className={`w-full text-left pl-9 pr-4 py-2.5 flex gap-2.5 hover:bg-white/5 transition-colors border-t border-theme/20 first:border-0 ${
                  !notif.read ? 'bg-yellow-500/5' : ''
                }`}
              >
                <span className="text-sm flex-shrink-0 mt-0.5">{typeIcon(notif.type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-1">
                    <p className={`text-[11px] font-medium leading-snug ${!notif.read ? 'text-primary' : 'text-secondary'}`}>
                      {notif.title}
                    </p>
                    <span className="text-[9px] text-muted flex-shrink-0 ml-1">{timeAgo(notif.created_at)}</span>
                  </div>
                  <p className="text-[10px] text-muted mt-0.5 line-clamp-2 leading-snug">
                    {getDisplay(notif.message)}
                  </p>
                </div>
                {!notif.read && (
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0 mt-1.5" />
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b border-theme">
        <div className="flex items-center gap-2">
          <span>🔔</span>
          <span className="text-sm font-bold text-primary">Notifications</span>
          {count > 0 && (
            <span className="text-[10px] font-bold text-red-400 bg-red-500/15 border border-red-500/25 rounded-full px-1.5 py-0.5">
              {count} unread
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleSound}
            title={soundEnabled ? 'Sound on — click to mute' : 'Sound off — click to unmute'}
            className={`text-[10px] px-2 py-1 rounded-full border transition-colors ${
              soundEnabled
                ? 'border-yellow-500/40 text-yellow-400 bg-yellow-500/10 hover:bg-yellow-500/20'
                : 'border-theme text-muted hover:text-primary'
            }`}
          >
            {soundEnabled ? '🔊' : '🔇'}
          </button>
          {count > 0 && (
            <button
              onClick={() => { markAllRead(); }}
              className="text-[10px] text-muted hover:text-primary transition-colors"
            >
              Mark all read
            </button>
          )}
          {readCount > 0 && (
            <button
              onClick={handleClearRead}
              disabled={clearing}
              className="text-[10px] text-muted hover:text-red-400 transition-colors disabled:opacity-50"
              title="Delete all read notifications"
            >
              {clearing ? '...' : 'Clear read'}
            </button>
          )}
        </div>
      </div>

      <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 240px)' }}>
        {notifications.length === 0 ? (
          <div className="text-center py-10">
            <div className="text-3xl mb-2">🔕</div>
            <p className="text-sm text-muted">No notifications yet</p>
          </div>
        ) : (
          <>
            {leadGroupList.length > 0 && (
              <>
                <div className="px-4 py-1.5 border-b border-theme/40">
                  <p className="text-[9px] font-semibold uppercase tracking-wider text-muted">Leads</p>
                </div>
                {leadGroupList.map(g => renderGroup(g, false))}
              </>
            )}

            {prospectGroupList.length > 0 && (
              <>
                <div className={`px-4 py-1.5 border-b border-theme/40 ${leadGroupList.length > 0 ? 'border-t border-theme/20' : ''}`}>
                  <p className="text-[9px] font-semibold uppercase tracking-wider text-muted">Prospects</p>
                </div>
                {prospectGroupList.map(g => renderGroup(g, true))}
              </>
            )}

            {nonGrouped.length > 0 && (
              <>
                {hasGroups && (
                  <div className="px-4 py-1.5 border-b border-theme/40 border-t border-theme/20">
                    <p className="text-[9px] font-semibold uppercase tracking-wider text-muted">Other</p>
                  </div>
                )}
                {nonGrouped.map(notif => (
                  <button
                    key={notif.id}
                    onClick={() => handleNotifClick(notif)}
                    className={`w-full text-left px-4 py-3 border-b border-theme/40 last:border-0 flex gap-3 hover:bg-white/5 transition-colors ${
                      !notif.read ? 'bg-yellow-500/5' : ''
                    }`}
                  >
                    <span className="text-lg flex-shrink-0 mt-0.5">{typeIcon(notif.type)}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-[11px] font-semibold leading-snug ${!notif.read ? 'text-primary' : 'text-secondary'}`}>
                          {notif.title}
                        </p>
                        <span className="text-[9px] text-muted flex-shrink-0">{timeAgo(notif.created_at)}</span>
                      </div>
                      <p className="text-[10px] text-muted mt-0.5 line-clamp-2 leading-snug">
                        {getDisplay(notif.message)}
                      </p>
                    </div>
                    {!notif.read && (
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0 mt-1.5" />
                    )}
                  </button>
                ))}
              </>
            )}
          </>
        )}
      </div>

      <div className="border-t border-theme/40 px-4 py-2.5">
        <button
          onClick={() => {
            onClose();
            const base = role === 'admin' ? '/dashboard/admin' : '/dashboard/sales';
            navigate(`${base}/notifications`);
          }}
          className="w-full text-center text-[11px] font-semibold text-yellow-400 hover:text-yellow-300 transition-colors py-1"
        >
          See all notifications →
        </button>
      </div>
    </>
  );
}
